import type { ChangeEvent } from 'react'
import { Link } from 'react-router-dom'
import { ShieldCheck } from 'lucide-react'
import { AuthLayout, OtpVerificationStep } from '@/modules/auth/ui/components'
import { formatTryAgainLabel } from '@/shared/ui/hooks'
import { Button, Input, SeparatedPhoneInput } from '@/shared/ui/primitives'
import { cn } from '@/shared/ui/cn'
import type { SendOtp, VerifyOtp, LoadOrgStatus } from '@/modules/auth/use-cases'
import { useSignInPage, type SetTokenOverride, type ClearTokenOverride } from './hooks/use-sign-in-page'

export type SignInPageProps = {
  onSendOtp: SendOtp
  onVerifyOtp: VerifyOtp
  onLoadOrgStatus: LoadOrgStatus
  onSetTokenOverride: SetTokenOverride
  onClearTokenOverride: ClearTokenOverride
}

export function SignInPage({
  onSendOtp,
  onVerifyOtp,
  onLoadOrgStatus,
  onSetTokenOverride,
  onClearTokenOverride,
}: SignInPageProps) {
  const vm = useSignInPage({
    onSendOtp,
    onLoadOrgStatus,
    onSetTokenOverride,
    onClearTokenOverride,
  })

  if (vm.step === 'checking') {
    return (
      <AuthLayout
        title="Checking your account status…"
        subtitle="Hang tight while we set things up."
      >
        <div
          data-testid="sign-in-checking"
          className="flex items-center justify-center py-6"
        >
          <span className="text-sm text-muted-foreground">Just a moment…</span>
        </div>
      </AuthLayout>
    )
  }

  if (vm.step === 'otp') {
    return (
      <OtpVerificationStep
        identifier={vm.identifier}
        channel={vm.currentMode}
        onSendOtp={onSendOtp}
        onVerifyOtp={onVerifyOtp}
        onVerifySuccess={vm.handleOtpSuccess}
        onBack={vm.backToIdentifier}
      />
    )
  }

  return (
    <AuthLayout title={vm.headline} subtitle={vm.subtitle}>
      <form onSubmit={vm.handleSubmit} className="flex flex-col gap-5" noValidate>
        {vm.isPhoneLoginEnabled ? (
          <div
            role="tablist"
            aria-label="Sign-in method"
            className="grid grid-cols-2 gap-1 rounded-md border border-border bg-secondary p-1 text-sm"
          >
            <button
              type="button"
              role="tab"
              aria-selected={vm.currentMode === 'email'}
              onClick={() => vm.setMode('email')}
              className={cn(
                'rounded-sm px-3 py-1.5 font-medium transition-colors',
                vm.currentMode === 'email'
                  ? 'bg-blaze-soft text-foreground shadow-sm ring-1 ring-blaze/30'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              Email
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={vm.currentMode === 'phone'}
              onClick={() => vm.setMode('phone')}
              className={cn(
                'rounded-sm px-3 py-1.5 font-medium transition-colors',
                vm.currentMode === 'phone'
                  ? 'bg-blaze-soft text-foreground shadow-sm ring-1 ring-blaze/30'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              Phone
            </button>
          </div>
        ) : null}
        {vm.currentMode === 'phone' ? (
          <div className="flex flex-col gap-1.5">
            <SeparatedPhoneInput
              id="phone"
              aria-label="Phone"
              value={vm.phoneE164}
              onChange={vm.setPhone}
              onBlur={vm.handleBlur}
              autoFocus
              placeholder="Phone"
              autoComplete="tel"
            />
            {vm.fieldError ? (
              <p data-testid="sign-in-field-error" className="text-xs text-destructive">
                {vm.fieldError}
              </p>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col gap-1.5">
            <Input
              id="email"
              type="email"
              aria-label="Email"
              value={vm.email}
              onChange={(e: ChangeEvent<HTMLInputElement>) => vm.setEmail(e.target.value)}
              onBlur={vm.handleBlur}
              placeholder="Email"
              autoComplete="email"
              autoFocus
            />
            {vm.fieldError ? (
              <p data-testid="sign-in-field-error" className="text-xs text-destructive">
                {vm.fieldError}
              </p>
            ) : null}
          </div>
        )}
        <p className="flex items-center gap-2 text-xs text-fg-dim">
          <ShieldCheck className="size-3.5 shrink-0" aria-hidden />
          We&apos;ll send you a 6-digit verification code — no password to remember.
        </p>
        {vm.errorMessage ? (
          <p role="alert" className="text-sm text-destructive">
            {vm.errorMessage}
          </p>
        ) : null}
        <Button
          type="submit"
          disabled={vm.isSubmitting || vm.isCooldownActive}
          className="w-full h-12"
        >
          {vm.isSubmitting
            ? 'Sending…'
            : vm.isCooldownActive
              ? formatTryAgainLabel(vm.cooldownSeconds)
              : 'Send Code'}
        </Button>
        {vm.showCreateClubFooter ? (
          <div
            data-testid="sign-in-footer"
            className="mt-5 flex flex-col gap-2 border-t border-border pt-7 text-center text-xs text-fg-dim"
          >
            <p>
              Need to set up your organization?{' '}
              <Link to="/register" className="font-semibold text-blaze underline-offset-4 hover:underline">
                Register Now
              </Link>
            </p>
            <p>
              <a
                href="https://blindbook.com"
                className="underline-offset-4 hover:text-foreground hover:underline"
              >
                Learn more about BlindBook&trade;
              </a>
            </p>
          </div>
        ) : null}
      </form>
    </AuthLayout>
  )
}
